/**
 * Cloud access for `strategy_drafts` <- localStorage `mycryptoplanner_buy_plan`
 * + `mycryptoplanner_sell_plan` + `mycryptoplanner_tax_harvest`.
 *
 * Single row per user (PK = user_id) holding the three "currently open" planner
 * drafts. Each draft is the raw form-state object (budget/futurePrice strings,
 * embedded currencyCode, ...) kept verbatim in its JSONB column — no coercion.
 * Any of the three may be null when that planner has not been opened.
 */
import type {
  BuyPlanState,
  SellPlanState,
  TaxHarvestState,
} from '../../context/StrategyContext';
import type { StrategyDraftsRow } from './rows';
import { supabase } from '../supabaseClient';
import { assertNoError } from './client';

const TABLE = 'strategy_drafts';

/** The three open drafts, mirroring StrategyContext's in-memory state. */
export interface StrategyDraftsValue {
  buyPlan: BuyPlanState | null;
  sellPlan: SellPlanState | null;
  taxHarvest: TaxHarvestState | null;
}

export function toRow(value: StrategyDraftsValue, userId: string): StrategyDraftsRow {
  return {
    user_id: userId,
    buy_plan: value.buyPlan,
    sell_plan: value.sellPlan,
    tax_harvest: value.taxHarvest,
  };
}

export function fromRow(row: StrategyDraftsRow): StrategyDraftsValue {
  return {
    buyPlan: (row.buy_plan ?? null) as BuyPlanState | null,
    sellPlan: (row.sell_plan ?? null) as SellPlanState | null,
    taxHarvest: (row.tax_harvest ?? null) as TaxHarvestState | null,
  };
}

/** Fetch the user's drafts row, or null when none exists yet. */
async function fetch(userId: string): Promise<StrategyDraftsValue | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  assertNoError(error, `fetch(${TABLE})`);
  return data ? fromRow(data as StrategyDraftsRow) : null;
}

async function upsert(userId: string, value: StrategyDraftsValue): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .upsert(toRow(value, userId) as any, { onConflict: 'user_id' });
  assertNoError(error, `upsert(${TABLE})`);
}

async function remove(userId: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('user_id', userId);
  assertNoError(error, `remove(${TABLE})`);
}

export const strategyDraftsTable = {
  toRow,
  fromRow,
  fetch,
  upsert,
  remove,
};
